<template>
    <div class="manual-live-demo-frame" aria-hidden="true">
        <div class="manual-live-demo-frame__scroll">
            <div class="manual-live-demo-frame__inner">
                <DataGrid
                    :prefix="prefix"
                    :columns="columns"
                    :actions="[]"
                    :show-export="false"
                    :show-search-button="false"
                >
                    <!-- INTERIM cell formatting: main grids format via their
                         page-level #cell slots which the island lacks. Remove
                         once column formatting has a single source of truth. -->
                    <template
                        v-for="column in dateColumns"
                        :key="column.slug"
                        #[`cell-${column.slug}`]="{ entry }"
                    >
                        {{ entry[column.slug] ? dateHumanReadable(entry[column.slug] as string) : '' }}
                    </template>
                    <template
                        v-for="column in plainColumns"
                        :key="column.slug"
                        #[`cell-${column.slug}`]="{ entry }"
                    >
                        {{ cellValue(entry[column.slug]) }}
                    </template>
                </DataGrid>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import DataGrid from '@src/Common/DataGrid/DataGrid.vue';
import type { Column } from '@src/Common/DataGrid/types';
import { dateHumanReadable } from '@src/Common/Helpers/dates';

const props = defineProps<{
    prefix: string;
    columns: Column[];
}>();

const dateColumns = computed(() =>
    props.columns.filter((column) => column.type === 'date' || column.type === 'datetime'),
);

// Columns with their own grid component (boolean, …) keep the default render.
const plainColumns = computed(() =>
    props.columns.filter(
        (column) => !column.gridComponent && column.type !== 'date' && column.type !== 'datetime',
    ),
);

function cellValue(value: unknown): string {
    if (Array.isArray(value)) {
        return value
            .map((item) =>
                item && typeof item === 'object'
                    ? String((item as Record<string, unknown>).name ?? (item as Record<string, unknown>).title ?? '')
                    : String(item),
            )
            .filter(Boolean)
            .join(', ');
    }

    if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;

        return String(record.name ?? record.title ?? '');
    }

    return value == null ? '' : String(value);
}
</script>

<style scoped>
.manual-live-demo-frame {
    position: relative;
}
.manual-live-demo-frame__scroll {
    overflow-x: auto;
    overflow-y: hidden;
    max-height: 420px;
}
/* Non-interactive demo: events fall through to the scrollable wrapper. */
.manual-live-demo-frame__inner {
    pointer-events: none;
    user-select: none;
}
/* Theme grids are w-full + table-fixed; inside the scroll frame the table
   must size to its content (auto layout, natural width) or the cyclic
   max-content resolution explodes to absurd widths. */
.manual-live-demo-frame :deep(.datatable-table) {
    table-layout: auto;
    width: max-content;
    min-width: 100%;
}
/* The wrapper clips overflow (overflow-x: hidden) — let it grow with the
   table so the outer frame is the single scroll surface. */
.manual-live-demo-frame :deep(.datatable-wrapper) {
    overflow: visible;
    width: max-content;
    min-width: 100%;
}
.manual-live-demo-frame :deep(.datatable-filters),
.manual-live-demo-frame :deep(.datatable-filters-advanced),
.manual-live-demo-frame :deep(.datatable-pagination__nav) {
    display: none;
}
.manual-live-demo-frame::after {
    content: '';
    pointer-events: none;
    position: absolute;
    inset: auto 0 0 0;
    height: 48px;
    background: linear-gradient(transparent, rgba(128, 128, 128, 0.08));
}
</style>
